import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, CheckCheck, MessageCircle, Sparkles, X } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { api } from "../lib/api";
import { notificationDestination } from "../lib/notification-navigation";

type Notification = {
  id: string;
  kind: string;
  title: string;
  titleAr: string;
  body: string;
  bodyAr: string;
  href?: string | null;
  readAt?: string | null;
  createdAt: string;
};

export function NotificationCenter() {
  const { isArabic } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const client = useQueryClient();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const { data = [], error } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => api<Notification[]>("/account/notifications"),
    refetchInterval: 30_000,
  });
  const unread = data.filter((item) => !item.readAt).length;
  useEffect(() => { if (open) panel.current?.querySelector<HTMLButtonElement>("button")?.focus(); }, [open]);
  const refresh = () => client.invalidateQueries({ queryKey: ["notifications"] });
  const readOne = useMutation({
    mutationFn: (id: string) => api(`/account/notifications/${id}/read`, { method: "PATCH" }),
    onSuccess: refresh,
  });
  const readAll = useMutation({
    mutationFn: () => api("/account/notifications/read-all", { method: "PATCH" }),
    onSuccess: refresh,
  });

  useEffect(() => {
    function outside(event: MouseEvent) {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, []);

  function openItem(item: Notification) {
    if (!item.readAt) readOne.mutate(item.id);
    setOpen(false);
    const destination = notificationDestination(item, user?.role);
    if (destination) {
      navigate(destination);
      const hash = destination.split("#")[1];
      if (hash) requestAnimationFrame(() => requestAnimationFrame(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" })));
    }
  }

  return (
    <div className="notification-center" ref={root} onKeyDown={event => { if (event.key === "Escape" && open) { event.stopPropagation(); setOpen(false); trigger.current?.focus(); } }} onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false); }}>
      <button ref={trigger} className="icon-button notification-trigger" aria-label={isArabic ? "الإشعارات" : "Notifications"} aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((value) => !value)}>
        <Bell />
        {unread > 0 && <i>{unread > 9 ? "9+" : unread}</i>}
      </button>
      {open && (
        <section ref={panel} id={panelId} className="notification-panel" aria-label={isArabic ? "الإشعارات" : "Notifications"}>
          <header>
            <div>
              <span className="eyebrow">{isArabic ? "مركز التحديثات" : "Update center"}</span>
              <h2>{isArabic ? "الإشعارات" : "Notifications"}</h2>
            </div>
            <button onClick={() => { setOpen(false); trigger.current?.focus(); }} aria-label={isArabic ? "إغلاق" : "Close"}>
              <X />
            </button>
          </header>
          {unread > 0 && (
            <button className="mark-all" onClick={() => readAll.mutate()} disabled={readAll.isPending}>
              <CheckCheck /> {isArabic ? "تحديد الكل كمقروء" : "Mark all as read"}
            </button>
          )}
          <div className="notification-list">
            {error ? (
              <p className="notification-error">{isArabic ? "تعذر تحميل الإشعارات الآن." : "Notifications could not be loaded."}</p>
            ) : data.length === 0 ? (
              <div className="notification-empty">
                <Sparkles />
                <b>{isArabic ? "أنت على اطلاع" : "You’re all caught up"}</b>
                <span>{isArabic ? "ستظهر الرسائل والتحديثات المهمة هنا." : "Messages and important updates will appear here."}</span>
              </div>
            ) : (
              data.map((item) => (
                <button key={item.id} className={item.readAt ? "notification-item" : "notification-item unread"} onClick={() => openItem(item)}>
                  <span className="notification-item__icon">{item.kind === "message" ? <MessageCircle /> : <Sparkles />}</span>
                  <span>
                    <b>{isArabic ? item.titleAr || item.title : item.title}</b>
                    <p>{isArabic ? item.bodyAr || item.body : item.body}</p>
                    <small>
                      {new Intl.RelativeTimeFormat(isArabic ? "ar" : "en", {
                        numeric: "auto",
                      }).format(Math.max(-30, Math.round((new Date(item.createdAt).getTime() - Date.now()) / 86400000)), "day")}
                    </small>
                  </span>
                  {!item.readAt && <i />}
                </button>
              ))
            )}
          </div>
        </section>
      )}
    </div>
  );
}
